<template>
  <pf-input-group class="x-input">
    <pf-text-input-group-utilities v-if="$slots.prepend || withCheckbox">
      <input v-if="withCheckbox" v-model="effectiveChecked" type="checkbox" :name="withCheckbox === true ? undefined : withCheckbox" value="1" />
      <slot name="prepend">{{ prepend }}</slot>
    </pf-text-input-group-utilities>

    <pf-text-input
      v-if="inputType !== 'hidden' && inputType !== 'color' && inputType !== 'file' && inputType !== 'range'"
      v-bind="$attrs"
      ref="input"
      key="input"
      :id="fieldId"
      :aria-label="ariaLabel ?? ($attrs['aria-label'] as string | undefined)"
      :model-value="inputValue"
      :type="showPassword ? 'text' : inputType"
      :step="step"
      :pattern="pattern ? pattern : typePattern"
      :title="title ? title : typeTitle"
      :disabled="effectiveDisabled"
      @change="nativeErrors ? reportValidity() : checkValidity()"
      @blur="checkValidity"
      @invalid="isInvalid = true"
      @input="onInput"
    />

    <slot name="append">
      <pf-input-group-text v-if="append">
        {{ append }}
      </pf-input-group-text>
      <pf-input-group-text v-if="$slots.icon">
        <slot name="icon" />
      </pf-input-group-text>
    </slot>

    <pf-tooltip v-if="type == $as<InputType>('new-password')">
      <pf-button :disabled="effectiveDisabled" @click="generatePassword">
        <template #icon><arrows-rotate-icon /></template>
      </pf-button>
      <template #content>{{ newPasswordLabel }}</template>
    </pf-tooltip>

    <pf-tooltip v-if="resetValue !== undefined">
      <pf-button :disabled="effectiveDisabled || resetValue === value || undefined" @click="reset">
        <template #icon><rotate-left-icon /></template>
      </pf-button>
      <template #content>{{ resetLabel }}</template>
    </pf-tooltip>

    <slot />
  </pf-input-group>

  <transition>
    <pf-form-helper-text v-if="help || (isInvalid && !nativeErrors && (title || typeTitle))">
      <pf-helper-text>
        <pf-helper-text-item v-if="isInvalid && !nativeErrors && (title || typeTitle)" variant="error">
          {{ title ? title : typeTitle }}
        </pf-helper-text-item>
        <pf-helper-text v-else>{{ help }}</pf-helper-text>
      </pf-helper-text>
    </pf-form-helper-text>
  </transition>
</template>

<style lang="scss">
.x-input .help-block {
  &.v-enter,
  &.v-leave-to {
    line-height: 0;
  }
}
</style>

<script lang="ts">
const PWD_NUMBERS = 3;
const PWD_UPPERCASE = 3;
const PWD_LENGTH = 12;

type PfTextInputProps = ComponentProps<typeof PfTextInput>;
type OtherTextInputAttributes = {
  autocomplete?: string;
};

interface Props<T extends InputType = 'text', N extends boolean = false>
  extends
    XFormElementProps<T, N>,
    /* @vue-ignore */ Omit<
      PfTextInputProps,
      | 'append'
      | 'prepend'
      | 'checked'
      | 'disabled'
      | 'type'
      | 'pattern'
      | 'step'
      | 'title'
      | 'modelValue'
      | 'modelModifiers'
      | 'onUpdate:modelValue'
      | 'autocomplete'
    >,
    /* @vue-ignore */ OtherTextInputAttributes {
  help?: string;
  nativeErrors?: boolean;
  newPasswordLabel?: string;
  resetLabel?: string;
}
</script>

<script lang="ts" setup generic="T extends InputType = 'text', N extends boolean = false">
import { pickRandom, shuffle, cast as $as } from './utils';
import type PfTextInput from '@vue-patternfly/core/components/TextInput.vue';
import { toValue, computed, type Ref, ref, useAttrs } from 'vue';
import ArrowsRotateIcon from '@vue-patternfly/icons/arrows-rotate-icon';
import RotateLeftIcon from '@vue-patternfly/icons/rotate-left-icon';
import type { ComponentExposed, ComponentProps } from 'vue-component-type-helpers';
import { useFormGroupField, useXFormElement, type InputType, type XFormElementEmits, type XFormElementProps } from './input';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<Props<T, N>>(), {
  checked: undefined,
  newPasswordLabel: 'Genera nuova password' as any,
  resetLabel: 'Reset' as any,
});

const emit = defineEmits<XFormElementEmits<T, N>>();

defineSlots<{
  default?: (props: Record<string, never>) => any;
  prepend?: (props: Record<string, never>) => any;
  append?: (props: Record<string, never>) => any;
  icon?: (props: Record<string, never>) => any;
}>();

const {
  change,
  checkValidity,
  effectiveChecked,
  effectiveDisabled,
  input,
  inputType,
  inputValue,
  isInvalid,
  reportValidity,
  reset,
  update,
  value,
  typeTitle,
  typePattern,
} = useXFormElement(props, emit);

const attrs = useAttrs();
const { id: fieldId, ariaLabel } = useFormGroupField(() => ({ ...attrs, title: props.title }));

function generatePassword() {
  const pwd = [];
  const allowed_chars = 'abcdefghjkmnpqrstuvwxyz'.split('');
  const allowed_nums = '23456789'.split('');

  const nums = Math.round(Math.random() * PWD_NUMBERS);
  for (let i = 0; i < nums; i++) {
    pwd.push(pickRandom(allowed_nums));
  }

  const upper = Math.round(Math.random() * PWD_UPPERCASE);
  for (let i = 0; i < upper; i++) {
    pwd.push(pickRandom(allowed_chars).toUpperCase());
  }

  for (let i = 0; i < PWD_LENGTH - nums - upper; i++) {
    pwd.push(pickRandom(allowed_chars));
  }

  update(shuffle(pwd).join(''));
  showPassword.value = true;
}

defineExpose({
  input: computed(() => toValue(input as Ref<ComponentExposed<typeof PfTextInput> | null>)?.input),
  checkValidity,
  reportValidity,
  reset,
});

const showPassword = ref(false);

function onInput(e: Event) {
  showPassword.value = false;
  change(e);
}
</script>
